import React, { useEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import "./VipUpgradePromo.styles.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const slides = [
  {
    id: 1,
    src: "/static/vip/upgrade/feature-1.gif",
    caption: {
      title: "Premium Close-Up LIVE Fireworks",
      subtitle: (
        <>
          Watch nightly fireworks over MK streamed from our brand new camera!
          Watch from 4 Magical Locations:
          <br />
          VIP Rooftop, Hotel Suite, VIP Cruise, or Castle View
        </>
      ),
    },
  },
  {
    id: 2,
    src: "/static/vip/upgrade/feature-3.gif",
    caption: {
      title: "Magical Multiview",
      subtitle:
        "Stroll the parks with your favorite LIVE streamers in one multi-view display! Catch everything happening around the parks from rides to shows all at the same time!",
    },
  },
  {
    id: 3,
    src: "/static/vip/upgrade/feature4.gif",
    caption: {
      title: "LIVE Piano Bar Request Show",
      subtitle:
        "Missing your favorite piano bar? Enjoy our monthly live show featuring your favorite theme park performers taking requests right in your living room!",
    },
  },
];

function NextArrow({ onClick }) {
  return (
    <div className="vip_upgrade_arrow vip_upgrade_next-arrow" onClick={onClick}>
      ▶
    </div>
  );
}
function PrevArrow({ onClick }) {
  return (
    <div className="vip_upgrade_arrow vip_upgrade_prev-arrow" onClick={onClick}>
      ◀
    </div>
  );
}

function VipUpgradePromo({ onUpgrade, onCancel }) {
  const [current, setCurrent] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [mutedFallback, setMutedFallback] = useState(false);
  const [pendingUnmutedAttempt, setPendingUnmutedAttempt] = useState(false);

  const settings = useMemo(
    () => ({
      centerMode: true,
      centerPadding: "50px",
      dots: true,
      infinite: true,
      speed: 400,
      slidesToShow: 1,
      afterChange: (i) => setCurrent(i),
      nextArrow: <NextArrow />,
      prevArrow: <PrevArrow />,
      lazyLoad: "ondemand",
      waitForAnimate: true,
      swipeToSlide: true,
      responsive: [
        { breakpoint: 768, settings: { centerMode: false, centerPadding: "0px" } },
      ],
    }),
    []
  );

  useEffect(() => {
    const { style } = document.body;
    const prev = style.overflow;
    style.overflow = showVideo ? "hidden" : prev || "";
    return () => { style.overflow = prev || ""; };
  }, [showVideo]);

  useEffect(() => {
    if (!showVideo || !pendingUnmutedAttempt) return;
    const v = videoRef.current;
    if (!v) return;
    (async () => {
      try {
        v.muted = false;
        v.volume = 0.5;
        await v.play();
        setMutedFallback(false);
        setIsPlaying(true);
      } catch {
        try {
          v.muted = true;
          await v.play();
          setMutedFallback(true);
          setIsPlaying(true);
        } catch {
          setIsPlaying(false);
        }
      } finally {
        setPendingUnmutedAttempt(false);
      }
    })();
  }, [showVideo, pendingUnmutedAttempt]);

  const pageBg = {
    backgroundImage: `url('/static/vip/upgrade/night.jpg')`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center top",
    backgroundSize: "contain",
    backgroundColor: "#000",
    minHeight: "100vh",
    width: "100%",
  };

  const overlayRoot = {
    position: "fixed",
    inset: 0,
    zIndex: 9990,
    backgroundImage: `url('/static/vip/upgrade/night.jpg')`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center center",
    backgroundSize: "cover",
    backgroundColor: "#000",
    display: "grid",
    placeItems: "center",
  };

  // Darken background behind the card
  const bgVeil = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.78)",
    zIndex: 9991,
    pointerEvents: "none",
  };

  // Centered “pop-out” card (smaller than screen)
  const card = {
  position: "relative",
  zIndex: 10000,
  width: "min(96vw, 1300px)",   // was 94vw / 1150px
  aspectRatio: "16 / 9",
  maxHeight: "min(88vh, 760px)",
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 20px 60px rgba(0,0,0,.6)",
  background: "#000",
  };


  // Video fills card completely (no side bars); may crop slightly (object-fit: cover)
  const videoEl = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center center",
    display: "block",
    backgroundColor: "#000",
  };

  const closeBtn = {
    position: "fixed",
    top: "calc(env(safe-area-inset-top, 0px) + 12px)",
    right: "calc(env(safe-area-inset-right, 0px) + 12px)",
    zIndex: 10001,
  };

  if (showVideo) {
    return (
      <div style={overlayRoot}>
        <div style={bgVeil} />
        <button
          className="vip_upgrade_close-button"
          onClick={() => {
            setShowVideo(false);
            setIsPlaying(false);
            setMutedFallback(false);
          }}
          aria-label="Close video"
          style={closeBtn}
        >
          ✕
        </button>

        <div style={card}>
          <video
            key="promo-video"
            ref={videoRef}
            className="vip_upgrade_tour-video"
            controls
            autoPlay
            playsInline
            preload="auto"
            controlsList="nodownload nofullscreen"
            disablePictureInPicture
            style={videoEl}
            onPlaying={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setShowVideo(false)}
            onError={() => {
              try {
                const v = videoRef.current;
                if (!v) return;
                v.muted = true;
                v.load();
                v.play().catch(() => {});
                setMutedFallback(true);
              } catch {}
            }}
          >
            <source
              src="https://cdn.mywaitserver.live/promodraft5.mp4"
              type="video/mp4"
            />
          </video>

          {isPlaying && mutedFallback && (
            <button
              onClick={() => {
                const v = videoRef.current;
                if (!v) return;
                try {
                  v.muted = false;
                  v.volume = 0.5;
                  const p = v.play();
                  if (p && typeof p.then === "function") p.catch(() => {});
                  setMutedFallback(false);
                } catch {}
              }}
              style={{
                position: "absolute",
                right: 12,
                bottom: 12,
                zIndex: 10002,
                padding: "10px 14px",
                borderRadius: 8,
                border: 0,
                background: "rgba(0,0,0,.65)",
                color: "#fff",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Tap to Unmute 
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={pageBg}>
      <button
        className="vip_upgrade_top-close-button"
        onClick={onCancel}
        aria-label="Close"
      >
        ✕
      </button>

      <div className="vip_upgrade_container">
        <h1 className="vip_upgrade_rainbow-title">
          <span className="vip_upgrade_white">VIP Package Upgrade!</span>&nbsp;
          <span className="vip_upgrade_pink">3 NEW Premium Features...</span>
        </h1>

        <button
          className="vip_upgrade_tour-button"
          onClick={() => {
            setPendingUnmutedAttempt(true);
            setShowVideo(true);
          }}
        >
          Take a Video Tour
        </button>

        <div className="vip_upgrade_carousel">
          <Slider {...settings}>
            {slides.map((slide) => (
              <div key={slide.id} className="vip_upgrade_slide">
                <img src={slide.src} alt={slide.caption.title} decoding="async" />
                <div className="vip_upgrade_caption">
                  <div className="vip_upgrade_main">{slide.caption.title}</div>
                  <div className="vip_upgrade_sub">{slide.caption.subtitle}</div>
                </div>
              </div>
            ))}
          </Slider>
        </div>

        <button className="vip_upgrade_vip-button" onClick={onUpgrade}>
          Unlock My VIP Access!
        </button>
      </div>
    </div>
  );
}

export default VipUpgradePromo;
